import { useContext, useEffect, useRef, useState } from "react";
import { ChatModel, ChatBubble } from "../../api/models/chatModel";
import Brending from "../Brending";
import { iconsImgs } from "../../utils/icons";
import './ChatPages.scss'
import { useLocation, useNavigate } from "react-router-dom";
import { localStorageKeys } from "../../utils/localStorageKeys";
import AuthenticationResponse from "../../api/responses/AuthenticationResponse";
import { getUserAvatar } from "../../utils/getMedia";
import { AzureBlob, HostChatMessage, Invitation, Offer, SendMessageCommandParameters } from "../../types";
import SignalRService from "../../ChatService/ChatService";
import { hubConnection } from "signalr-no-jquery";
import { SignalRContext } from "../../GlobalContext/signalRContext";
import { PartiesContext } from "../../GlobalContext/partiesContext";
import { Modal } from "react-bootstrap";
import AuthClient from "../../api/AuthClient";
import GetSASTokenApi from "../../api/GetSASTokenApi";
import GetHostChatMessages from "../../api/GetHostChatMessagesApi";
import ModalAttachmentInput from "./ModalAttachmentInput";
import { uploadImageToStorage } from "../../azureBlob/fileUpload";
import { SelectedImageContext } from "../../SelectedImageContext";
import chatAttachmentType from "../../api/enums/ChatAttachmentType";
import handleImgError from "../../handleImgError";

const normalizeChatMessage = (message: unknown): string => {
  if (typeof message === "string") {
    return message;
  }

  if (message === null || message === undefined) {
    return "";
  }

  return String(message);
};

const ChatPages = () => {

  const bottomRef = useRef<HTMLDivElement>(null);
  const [chatModel, setChatModels] = useState<ChatModel>();
  const [messageInput, setMessageInput] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [connected, setOnConnected] = useState<boolean>(false);
  const [apiMessages, setApiMessages] = useState<HostChatMessage[] | null>(null);
  // Queue of messages typed while SignalR is disconnected (initial connect or mid-session drop).
  // Flushed by the useEffect watching `connected` once the connection is ready.
  const [pendingMessages, setPendingMessages] = useState<SendMessageCommandParameters[]>([]);

  const storedLoginResponse =
    sessionStorage.getItem(localStorageKeys.loginResponse) ??
    localStorage.getItem(localStorageKeys.loginResponse) ??
    "{}";
  const userInfo: AuthenticationResponse = JSON.parse(storedLoginResponse);
  const currentUserName = `${userInfo.firstName} ${userInfo.lastName}`
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;

  const { chatAttachment, setAttachmentFile } = useContext(SelectedImageContext);
  const location = useLocation();
  const navigate = useNavigate();

  const chatRouteState = location.state as {
    messages?: HostChatMessage[];
    offer?: Offer;
    groupAdminName?: string;
    groupAdminId?: string;
    promoterName?: string;
    promoterId?: string;
    currentUserId?: string;
    returnTo?: string;
  } | null;

  const messages = chatRouteState?.messages ?? [];
  const offer = chatRouteState?.offer;
  const groupAdminName = chatRouteState?.groupAdminName ?? "";
  const groupAdminId = chatRouteState?.groupAdminId ?? "";
  const promoterName = chatRouteState?.promoterName ?? "";
  const promoterId = chatRouteState?.promoterId ?? "";
  const currentUserId = chatRouteState?.currentUserId;
  const toUserId = groupAdminId === currentUserId ? promoterId : groupAdminId;
  const returnTo = chatRouteState?.returnTo;
  const displayedMessages = apiMessages ?? messages;

  // Stable refs so the connection, hub proxy, and SignalR service survive re-renders.
  // Previously these were created on every render, which meant handleSendMessage was
  // calling sendMessage on a different service instance than the one that had connected.
  const signalrConnectionRef = useRef<ReturnType<typeof hubConnection> | null>(null);
  const hubProxyRef = useRef<any>(null);
  const signalRServiceRef = useRef<SignalRService | null>(null);
  if (!signalrConnectionRef.current) {
    signalrConnectionRef.current = hubConnection(process.env.REACT_APP_CHAT_URL);
    hubProxyRef.current = signalrConnectionRef.current.createHubProxy('ChatHub');
    signalRServiceRef.current = new SignalRService(
      signalrConnectionRef.current,
      hubProxyRef.current,
      setOnConnected
    );
  }
  const hubProxy = hubProxyRef.current;
  const signalRService = signalRServiceRef.current!;
  const { isConnected, setSignalRConnectionState } = useContext(SignalRContext);

  const contactInfoNotAllowed = "(CONTACT INFO NOT ALLOWED IN CHAT)";
  const isMediaMessage = (message: unknown) =>
    typeof message === "string" && !!mediaUrl && message.startsWith(mediaUrl);

  const acceptedOfferId: string = offer?.id ?? "";
  const { myPartiesInfo } = useContext(PartiesContext);
  const partyForOffer =
    offer &&
    myPartiesInfo.find((p) =>
      p.promotersOffers.some((po) =>
        po.offers.some((o) => o.id === offer.id)
      )
    );
  const selfUserId =
    partyForOffer?.invitations?.find(
      (el: Invitation) =>
        el.firstName === userInfo.firstName &&
        el.lastName === userInfo.lastName
    )?.id ??
    currentUserId ??
    chatModel?.userId;
  const selfUserIdRef = useRef<string | undefined>(undefined);
  selfUserIdRef.current = selfUserId;

  const [showModal, setShowModal] = useState(false);
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const showAttachmentInputModals = () => {

    handleShowModal();

    const authClient = new AuthClient();
    const getSasTokenApi = new GetSASTokenApi(authClient);

    getSasTokenApi.getSasToken(false, "0", 0).then((sasToken) => {
      const azureBlob: AzureBlob = sasToken.data;
      sessionStorage.setItem("azureBlob", JSON.stringify(azureBlob));
    });
  }

  const updateChatModel = (message: string, senderDisplayName: string, fromUserId: string) => {
    setChatModels((prevChatModel) => {
      if (prevChatModel) {
        const lastBubble = prevChatModel.bubbles[prevChatModel.bubbles.length - 1];
        if (
          lastBubble &&
          lastBubble.senderName === senderDisplayName &&
          lastBubble.message === message
        ) {
          return prevChatModel;
        }

        const newBubble = new ChatBubble(senderDisplayName, "Just now", message, "", new Date(), fromUserId);
        const updatedBubbles = [...prevChatModel.bubbles, newBubble];
        return { ...prevChatModel, bubbles: updatedBubbles };
      }

      return prevChatModel;
    });
  };

  // Replaces a locally-rendered bubble's text with a new value.
  // When `originalMessage` is provided, the most recent bubble whose message
  // matches is targeted — important when multiple messages have been queued
  // (e.g. during offline compose) and the server's blocked-message ack may
  // come back for an older queued message, not the last one.
  // Falls back to "last bubble" when no original is supplied for safety.
  const updateBubbleMessage = (newMessage: string, originalMessage?: string) => {
    setChatModels((prevChatModel) => {
      if (prevChatModel) {
        let targetIndex = -1;
        if (originalMessage !== undefined) {
          for (let i = prevChatModel.bubbles.length - 1; i >= 0; i--) {
            if (prevChatModel.bubbles[i].message === originalMessage) {
              targetIndex = i;
              break;
            }
          }
        }
        if (targetIndex === -1) {
          targetIndex = prevChatModel.bubbles.length - 1;
        }

        if (targetIndex >= 0) {
          const updatedBubbles = [...prevChatModel.bubbles];
          updatedBubbles[targetIndex].message = newMessage;

          return { ...prevChatModel, bubbles: updatedBubbles };
        }
      }
      return prevChatModel;
    });
  };

  const onMessage = (response: any) => {
    console.log("Received message via SignalR:", response);
    if (!response.Message && response.AttachmentLink) {
      response.Message = response.AttachmentLink
    }

    const normalizedMessage = normalizeChatMessage(response.Message);

    if (response.FromUserId !== selfUserIdRef.current) {

      if (normalizedMessage.trim() !== "") {
        const updatedMessage = normalizedMessage.startsWith("blocked:") ? contactInfoNotAllowed : normalizedMessage;

        if (response.FromUserId === groupAdminId) {
          updateChatModel(updatedMessage, groupAdminName, response.FromUserId);
        } else if (response.FromUserId === promoterId) {
          updateChatModel(updatedMessage, promoterName, response.FromUserId);
        } else {
          updateChatModel(updatedMessage, "Clubbable", response.FromUserId);
        }
      }
    } else {

      if (normalizedMessage.trim() !== "") {
        const isBlockedMessage = normalizedMessage.startsWith("blocked:");

        if (isBlockedMessage) {
          // response.Message is "blocked:<original text>"; extract the original
          // so we can target the exact local bubble that was optimistically
          // added — critical when multiple messages were queued offline and
          // the server ack may not apply to the most recent bubble.
          const originalText = normalizedMessage.substring("blocked:".length);
          updateBubbleMessage(contactInfoNotAllowed, originalText);
          setModalOpen(true);
        }
      }
    }
  }

  useEffect(() => {
    if (!offer?.id) {
      setApiMessages(null);
      return;
    }

    let isMounted = true;
    const hostChatMessages = new GetHostChatMessages(new AuthClient());

    hostChatMessages.getChatMessages(offer.id).then((response) => {
      if (!isMounted) {
        return;
      }

      setApiMessages(response.data ?? []);
    }).catch(() => {
      if (isMounted) {
        setApiMessages(null);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [offer?.id]);

  useEffect(() => {
    if (!offer || !groupAdminName) return;
    const data = ChatModel.getData(
      displayedMessages,
      offer,
      groupAdminName,
      currentUserId ?? "",
      toUserId ?? "",
      groupAdminId
    );
    setChatModels(data);
  }, [displayedMessages, offer, groupAdminName, currentUserId, toUserId, groupAdminId]);

  useEffect(() => {
    if (!offer || !groupAdminName) {
      navigate(-1);
    }
  }, [offer, groupAdminName, navigate]);

  useEffect(() => {
    hubProxy.on('addMessage', onMessage);

    return () => {
      if (typeof hubProxy.off === "function") {
        hubProxy.off('addMessage', onMessage);
      }
    };
  }, [hubProxy, groupAdminId, groupAdminName, promoterId, promoterName]);

  useEffect(() => {
    if (chatModel && !isConnected && acceptedOfferId) {
      setSignalRConnectionState(true);
      signalRService.startConnection(acceptedOfferId);
    }
  }, [acceptedOfferId, chatModel, isConnected, setSignalRConnectionState, signalRService]);

  // Flush queued messages whenever SignalR (re)connects. This handles both
  // the initial connect and mid-session reconnects after a dropped connection.
  // Dep is intentionally just [connected] — we only want to flush on the
  // disconnected→connected transition, not on every pendingMessages update
  // (those happen while still disconnected and should stay queued).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (connected && pendingMessages.length > 0) {
      // Snapshot and clear the queue up-front so any new messages typed during
      // the flush end up in a fresh queue (no double-send).
      const queue = pendingMessages;
      setPendingMessages([]);
      (async () => {
        for (let i = 0; i < queue.length; i++) {
          try {
            await signalRService.sendMessage(queue[i]);
          } catch (err) {
            // If a send fails, re-enqueue the failed message plus the
            // remaining ones at the front of the queue so the next connect
            // transition will retry them (messages typed after the failure
            // while still `connected` have been added to `pendingMessages`
            // already and should come after the retry batch).
            console.error('Failed to flush queued chat message, re-enqueueing:', err);
            const remaining = queue.slice(i);
            setPendingMessages((prev) => [...remaining, ...prev]);
            return;
          }
        }
      })();
    }
  }, [connected]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatModel, chatModel?.bubbles]);

  //Send message
  const handleSendMessage = () => {

    if (messageInput.trim() !== "") {

      updateChatModel(messageInput, currentUserName, currentUserId ?? "");

      if (chatModel?.toUserId && chatModel.offerId) {
        const message: SendMessageCommandParameters = {
          FromUserId: chatModel.userId,
          ToUserId: chatModel?.toUserId,
          Message: messageInput,
          OfferId: chatModel.offerId,
          ChatRoomId: acceptedOfferId,
          MessageDate: new Date().toISOString(),
          CreatedAt: new Date().toISOString()
        }

        if (connected) {
          signalRService.sendMessage(message);
        } else {
          // Not connected yet (initial connect in progress or mid-session drop).
          // Queue the message — the flush useEffect will send it once connected.
          setPendingMessages((prev) => [...prev, message]);
        }
      }

      setMessageInput("");
    }
  };

  // Send attachment
  const SendAttachmentInChat = () => {

    const sasUrl = sessionStorage.getItem("azureBlob");
    if (!sasUrl) {
      return false;
    }
    const azureBlobs: AzureBlob[] = JSON.parse(sasUrl);

    uploadImageToStorage(chatAttachment, azureBlobs).then(() => {

      const attachmentLink = mediaUrl + azureBlobs[0].id;
      updateChatModel(attachmentLink, currentUserName, currentUserId ?? "");

      if (chatModel?.toUserId && chatModel.offerId) {

        const message: SendMessageCommandParameters = {
          FromUserId: chatModel.userId,
          ToUserId: chatModel?.toUserId,
          OfferId: chatModel.offerId,
          ChatRoomId: acceptedOfferId,
          MessageDate: new Date().toISOString(),
          CreatedAt: new Date().toISOString(),
          AttachmentLink: attachmentLink,
          AttachmentType: chatAttachmentType.InternalImage,
          ThumbnailLink: mediaUrl + azureBlobs[1].id
        }

        if (connected) {
          signalRService.sendMessage(message);
        } else {
          setPendingMessages((prev) => [...prev, message]);
        }
      }

      setAttachmentFile(null);
    });
  }

  useEffect(() => {
    return () => {
      if (isConnected) {
        setSignalRConnectionState(false);
      }
    };
  }, [location.pathname]);

  if (!offer || !groupAdminName) {
    return (
      <>
        <Brending />
      </>
    );
  }

  return (
    <>
      <Brending />

      <Modal centered show={isModalOpen} onHide={() => setModalOpen(false)}>
        <Modal.Header className="justify-content-center">
          Keep all contact in app
        </Modal.Header>

        <Modal.Body className="text-start">
          The promoter are not allowed to contact you outside the app. This is for your protection since they will never stop messaging you to get you to go out with them.
          Also, once they get your details they will tell you not to use the Clubbable app so they can keep them to themselves. We are making this app so you will have
          the best selection of offers from all promoters and club managers and that you only get offers when you actually do want to go out.
        </Modal.Body>

        <Modal.Footer>
          <button
            className="link btns btnMO justify-content-center d-flex  align-content-center  col-12 mt-2 mb-2"
            onClick={() => setModalOpen(false)}
          >
            Ok
          </button>
        </Modal.Footer>
      </Modal>

      {chatModel && (
        <div className="centerGroup">
          <div className="desktopUI ms-5 me-5">
            <div className="d-flex justify-content-start space col-6 backBtnChat">
              <button
                className='BtnTop yellowTxt btn text-decoration-none backBtn'
                onClick={() => {
                  if (returnTo) {
                    navigate(returnTo);
                    return;
                  }
                  navigate(-1);
                }}
              >
                Back
              </button>
            </div>
            <div className="containerChat">
              <div className="row">
                <div className="col-auto p-2">
                  <img src={getUserAvatar(`${chatModel.groupAdminId}?${Date.now()}`)}
                    alt=""
                    className="chatUserImg rounded-circle m-2"
                    onError={(event) => handleImgError(event, 'female')} />
                </div>

                <div className="col align-self-center headerText">
                  <p className="chatHeaderTitle p-0 m-0">{chatModel.headerTitle}</p>
                  <p className="chatHeaderSub yellowTxt p-0 m-0">at {chatModel.headerSub}</p>
                  {chatModel.lastSeen ? <p className="chatDate p-0 m-0">Last seen at {chatModel.lastSeen}</p> : ""}
                </div>
              </div>


              {chatModel.bubbles.length > 0 ? (
                chatModel.bubbles.map((bubble, index) => {
                  if (bubble.fromUserId === currentUserId) {
                    return (
                      <>
                        <div key={index} className="chatBubbleRight">
                          <div className="d-flex justify-content-end me-3">
                            <div className="talkBubbleRight triRight round btmRight">
                              <div className="talktext">
                                {isMediaMessage(bubble.message) ? (
                                  <img src={bubble.message} alt="Image" className="img-attachment img-fluid" />

                                ) : (
                                  <p className="text-white m-0 text-start">{bubble.message}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="row">
                            <div className="d-flex p-0 col align-items-center">
                              <div className="col seenTimeLeft d-flex p-3  align-items-center justify-content-end timeSpanRight">
                                <img className="chatCheck" src={iconsImgs.ChatCheck} alt="" />
                                <p className="m-0">{bubble.timestamp}</p>
                              </div>
                              <p className="userNameBubble m-2">{bubble.senderName}</p>
                            </div>

                            <div className="col-auto m-2 p-0 chatUserImgBubbleRight">
                              <img src={getUserAvatar(`${bubble.fromUserId}?${Date.now()}`)}
                                alt=""
                                className=" chatUserImgBubble rounded-circle"
                                onError={(event) => handleImgError(event, 'female')} />
                            </div>
                          </div>
                        </div>
                      </>

                    );
                  } else {

                    return (
                      <>
                        <div key={index} className="chatBubbleLeft">
                          <div className="talkBubble triRight round btmLeft">
                            <div className="talktext">
                              {isMediaMessage(bubble.message) ? (
                                <img src={bubble.message} alt="Image" className="img-attachment img-fluid" />

                              ) : (
                                <p className="text-white m-0 text-start">{bubble.message}</p>
                              )}
                            </div>
                          </div>

                          <div className="row">
                            <div className="chatUserImgBubble col-auto mt-2 m-2">
                              <img
                                src={getUserAvatar(`${bubble.fromUserId}?${Date.now()}`)}
                                alt=""
                                className="chatUserImgBubble rounded-circle"
                                onError={(event) => handleImgError(event, 'female')}
                              />
                            </div>

                            <div className="col p-0 align-self-center ms-2">
                              <div className="d-flex p-0 justify-content-start align-items-center">
                                <p className="userNameBubble m-0">{bubble.senderName}</p>
                                <div className="col d-flex p-3 justify-content-start align-items-center timeSpanLeft">
                                  <p className="seenTime m-0">{bubble.timestamp}</p>
                                  <img className="chatCheck" src={iconsImgs.ChatCheck} alt="" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  }
                })
              ) : (
                <div className="text-center p-4">
                  <p className="text-white">No messages yet. Send a message to start the conversation.</p>
                </div>
              )}
            </div>

            <div ref={bottomRef}></div>
            <div className="chatInput">
              <div className="chatInputWrapper">
                <div className="cameraIcon">
                  <img
                    src={iconsImgs.ChatCamera}
                    onClick={showAttachmentInputModals}
                  />
                </div>
                <div className="chatText">
                  <input
                    type="text"
                    placeholder="Your message here..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                </div>
                <div className="send-icon" onClick={handleSendMessage}>
                  <img src={iconsImgs.Send} />
                </div>
              </div>
              <ModalAttachmentInput
                showModal={showModal}
                handleCloseModal={handleCloseModal}
                SendAttachmentInChat={SendAttachmentInChat}
              />

            </div>

          </div>
        </div>
      )}

    </>
  );
};

export default ChatPages;
